import { lazy } from 'react';
import Clarity from '@microsoft/clarity';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom'
import { WebStorageStateStore } from 'oidc-client-ts';
import { Provider } from 'react-redux';
import store from "./store/store";
import { AuthProvider } from "react-oidc-context";
import { getBaseUrl, getSubdomain } from './utils/utils.js';
import AppLayout from "./layout/AppLayout";
import Contact from "./pages/contact/Contact.jsx";

// Reloads once when a lazy chunk 404s after a new deployment invalidates old hashes.
function lazyWithRetry<T extends React.ComponentType<unknown>>(
    factory: () => Promise<{ default: T }>
) {
    return lazy(() =>
        factory().catch((err) => {
            const reloadKey = 'chunk_reload_attempted';
            if (!sessionStorage.getItem(reloadKey)) {
                sessionStorage.setItem(reloadKey, '1');
                window.location.reload();
                // return a never-resolving promise so React suspense keeps showing the fallback
                return new Promise<never>(() => {});
            }
            throw err;
        })
    );
}

import {
    AppErrorWrapper,
    ComponentErrorWrapper,
    DashboardErrorWrapper,
    GeneralErrorWrapper
} from "@pages/error/Errors";
import Layout from "./layout/layout.jsx";


// LegacyViewer loaded lazily — see route definition below
import UseCase from './pages/useCase/UseCase';
import Home from "./pages/home/Home.jsx";
import FAQPopup from "./components/faqs.jsx";
import { RequireAuth } from '@components/RequireAuth';
import { RequireUser } from '@components/RequireUser';
import { RequireEsriConfig } from '@/components/RequireEsriConfig';
import NotFound from './pages/notFound/NotFound';

// Private route components — loaded lazily, only after authentication
const DashboardLayout = lazyWithRetry(() => import('./layout/DashboardLayout.jsx'));
const PostLogin = lazyWithRetry(() => import('./pages/postLogin/PostLogin.jsx').then(m => ({ default: m.PostLogin })));
const Dashboard = lazyWithRetry(() => import('./pages/dashboard/Dashboard.jsx'));
const Users = lazyWithRetry(() => import('./pages/users/users.jsx'));
const CustomerManagement = lazyWithRetry(() => import('./pages/customerManagement/customerManagement.jsx'));
const CreditPage = lazyWithRetry(() => import('./pages/credits/credit.jsx'));
const FinancePage = lazyWithRetry(() => import('./pages/finance/finance.jsx'));
const CustomerSettings = lazyWithRetry(() => import('./pages/customerSettings/CustomerSettings'));
const Projects = lazyWithRetry(() => import('./pages/projects/projects.jsx').then(m => ({ default: m.Projects })));
const ProjectSettings = lazyWithRetry(() => import('./pages/projects/projectSettings.jsx').then(m => ({ default: m.ProjectSettings })));
const ProjectAdministration = lazyWithRetry(() => import('./pages/projects/projectAdministration.jsx').then(m => ({ default: m.ProjectAdministration })));
const AIAdministation = lazyWithRetry(() => import('./pages/aiAdministration/aiAdministration.jsx').then(m => ({ default: m.AIAdministation })));
const ProjectDetailsTab = lazyWithRetry(() => import('./components/projectDetailTab.jsx'));
const ProjectCreateTab = lazyWithRetry(() => import('./components/projectCreateTab.jsx'));
const ProjectBoundaryTab = lazyWithRetry(() => import('./components/projectBounadaryTab.jsx'));
const ProjectUserAccess = lazyWithRetry(() => import('@/pages/projects/ProjectUserAccess'))
const ProjectProcessTab = lazyWithRetry(() => import('@/pages/projects/ProjectProcessTab'))
const ProjectGND = lazyWithRetry(() => import('./components/gnddashboard/projectGnd'));
const ProjectGndLayerTab = lazyWithRetry(() => import('./components/gnddashboard/projectGndLayerTab'));
const ProjectGndGroupTab = lazyWithRetry(() => import('./components/gnddashboard/projectGndGroupTab'));
const ProjectGndExternalServicesTab = lazyWithRetry(() => import('./components/gnddashboard/ProjectGndExternalServicesTab'));
const CreditProcessTab = lazyWithRetry(() => import('./components/credits/CreditProcessTab').then(m => ({ default: m.CreditProcessTab })));
const CreditContainer = lazyWithRetry(() => import('./containers/creditContainer/creditContainer'));

export const getAvailableIDPs = () => {
    switch (getSubdomain()) {
        case "rhb":
            return ['AZRHB']
        case "db":
            return ['AZDBEC']
        case "cfl":
            return ['AZCFL']
        case "staging":
            return ['AZDBEC', 'INFRAVIEW_X2BIM']
        case "sbb.staging": //Staging SBB
            return ['SBB']
        case "sbb":         //Production SBB
            return ['SBB']
        case "bahn":
            return ['AZDB']
        default:
            return ['INFRAVIEW_X2BIM']
    }
}

export const getClarityProjectId = () => {

    if (window.location.hostname.includes('localhost')) {
        return "pgc28bvv5x" // use Staging host
    }
    switch (getSubdomain()) {
        case "db":
            return "pgv29m7u7o"
        case "cfl":
            return "pgv33d1d4i"
        case "staging":
            return "pgc28bvv5x"
        default:
            return "pgv2sjslkf"
    }
}

const baseOidcConfig = {
    authority: `${getBaseUrl()}/auth/realms/X2BIM`,
    client_id: "X2BIMWEB",
    redirect_uri: `${window.location.origin}/postLogin`,
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    post_logout_redirect_uri: window.location.origin,
}

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path={"/"} element={<Layout />} errorElement={<GeneralErrorWrapper />}>
            {/* <Route path="/queue" element={<Queue />} /> */}
            <Route element={<AppLayout />}>
                <Route index element={<Home />} errorElement={<AppErrorWrapper />} />
                {/* <Route path='pricing' element={<Pricing />} /> */}
                <Route path=':language/usecase' element={<UseCase />} />

                <Route path='contact' element={<Contact />} />
            </Route>
            <Route element={<RequireAuth />}>
                <Route element={<RequireUser />}>
                    <Route element={<RequireEsriConfig />}>
                    <Route element={<DashboardLayout />}>
                        <Route path="postLogin" element={<PostLogin />}
                            errorElement={<DashboardErrorWrapper errorOrigin="Dashboard" />} />
                        <Route path="dashboard" element={<Dashboard />}
                            errorElement={<DashboardErrorWrapper errorOrigin="Dashboard" />} />
                        <Route path="viewer" lazy={async () => {
                            let { default: LegacyViewer } = await import('./viewer/presentation/react/legacy/LegacyViewer');
                            return { Component: LegacyViewer };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Dashboard" />} />
                        <Route path="3dlabeling" lazy={async () => {
                            let { default: LabelingViewerPage } = await import("./viewer/presentation/react/labeling/LabelingViewerPage");
                            return { Component: LabelingViewerPage };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Viewer" />} />
                        <Route path="sbb" lazy={async () => {
                            let { default: SBBViewerPage } = await import("./viewer/presentation/react/sbb/SBBViewerPage");
                            return { Component: SBBViewerPage };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Viewer" />} />
                        <Route path="betaviewer" lazy={async () => {
                            let { BetaViewerLoader } = await import("./betaviewer/presentation/BetaViewer");
                            return { Component: BetaViewerLoader };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Viewer" />} />
                        <Route path="bim" lazy={async () => {
                            let { default: BimViewerPage } = await import("./viewer/presentation/react/bimviewer/BimViewerPage");
                            return { Component: BimViewerPage };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Viewer" />}>
                        </Route>
                        <Route path="nextgen" lazy={async () => {
                            let { default: NextGenViewerPage } = await import("./viewer/presentation/react/nextgen/NextGenViewerPage");
                            return { Component: NextGenViewerPage };
                        }} errorElement={<DashboardErrorWrapper errorOrigin="Viewer" />} />
                        
                        <Route path="projects" errorElement={<DashboardErrorWrapper errorOrigin="Project" />}>
                            <Route path="new" element={<ProjectCreateTab />}
                                errorElement={<DashboardErrorWrapper errorOrigin="CreateProject" />} />
                            <Route element={<Projects />}>
                                <Route
                                    index
                                    element={<ProjectDetailsTab />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="ProjectDetails" />}
                                />
                                <Route path="gnd" element={<ProjectGND />} errorElement={<ComponentErrorWrapper errorOrigin="gnd" />}>
                                    <Route
                                        index
                                        element={<ProjectGndLayerTab />}
                                        errorElement={<ComponentErrorWrapper errorOrigin="gndDashboard" />}
                                    />
                                    <Route
                                        path="groups"
                                        element={<ProjectGndGroupTab />}
                                        errorElement={<ComponentErrorWrapper errorOrigin="gndDashboardProcess" />}
                                    />
                                    <Route
                                        path="external-services"
                                        element={<ProjectGndExternalServicesTab />}
                                        errorElement={<ComponentErrorWrapper errorOrigin="gndExternalServices" />}
                                    />
                                </Route>
                                <Route
                                    path="boundary"
                                    element={<ProjectBoundaryTab />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="boundary" />}
                                />
                                <Route
                                    path="userAccess"
                                    element={<ProjectUserAccess />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="userAccess" />}
                                />
                                <Route
                                    path="processUserAccess"
                                    element={<Navigate to="../processes" replace />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="processUserAccess" />}
                                />
                                <Route
                                    path="processes"
                                    element={<ProjectProcessTab />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="Processes" />}
                                />
                                <Route path="process_copy" element={<Navigate to="../processes" />} />
                                <Route
                                    path="settings"
                                    element={<ProjectSettings />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="settings" />}
                                />
                                <Route
                                    path="administration"
                                    element={<ProjectAdministration />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="administration" />}
                                />
                                <Route
                                    path="aiClasses"
                                    element={<AIAdministation />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="AIAdministration" />}
                                />
                            </Route>
                        </Route>
                        <Route path="users" element={<Users />}
                            errorElement={<DashboardErrorWrapper errorOrigin="Dashboard" />} />
                        <Route path="credits" errorElement={<DashboardErrorWrapper errorOrigin="Credit" />}>
                            <Route element={<CreditPage />}>
                                <Route index element={<CreditContainer />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="CreditContainer" />} />
                                <Route path="processes" element={<CreditProcessTab />}
                                    errorElement={<ComponentErrorWrapper errorOrigin="CreditProcessTab" />} />

                            </Route>
                        </Route>
                        <Route path="finance-controlling" element={<FinancePage />}
                            errorElement={<DashboardErrorWrapper errorOrigin="Finance" />} />
                        <Route path="customer-user-admin" element={<CustomerManagement />}
                            errorElement={<DashboardErrorWrapper errorOrigin="customer-admin" />} />
                        <Route path="customer-settings" element={<CustomerSettings />}
                            errorElement={<DashboardErrorWrapper errorOrigin="customer-settings" />} />
                    </Route>
                    </Route>
                </Route>
                {/* <Route path="/user-access" element={<UserAccess />} /> */}
            </Route>
            <Route path="faq" element={<FAQPopup />} errorElement={<DashboardErrorWrapper errorOrigin="FAQ" />} />
            {/* Using path="*"" means "match anything", so this route
                acts like a catch-all for URLs that we don't have explicit
                routes for. */}
            <Route path="*" element={<NotFound />} />
        </Route>
    )
)

const onSigninCallback = () => {
    window.history.replaceState(
        {},
        document.title,
        window.location.pathname
    )
}

const App = () => {
    return (
        <AuthProvider {...baseOidcConfig} onSigninCallback={onSigninCallback}>
            <Provider store={store}>
                <RouterProvider router={router} />
            </Provider>
        </AuthProvider>
    )
}

Clarity.init(getClarityProjectId());

export default App
